import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncSelect from "react-select/async";

import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { getOrganisations } from "@/features/organisations/api/get-organisations";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn } from "@/lib/utils";

import { AddSessionFormData } from "../types/add-session-schema";
import { OrganisationOption } from "../types/organisation-option";

import { OrganisationSelectOption } from "./organisation-select-option";

export function OrganisationsSearchElement() {
  const form = useFormContext<AddSessionFormData>();
  const { organisation } = useOrganisation();
  const [value, setValue] = useState<string>("");

  const addOrganisation = (organisation: OrganisationOption) => {
    form.setValue("invitedOrganisations", [
      ...(form.getValues("invitedOrganisations") || []),
      organisation,
    ]);
    form.trigger("invitedOrganisations");
  };

  const organisationsError = form.formState.errors.invitedOrganisations;
  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!organisationsError && "border-destructive");
    },
  });

  const selectedOrganisations = form.getValues("invitedOrganisations");

  const loadOptions = async (input: string): Promise<OrganisationOption[]> => {
    const organisations = await getOrganisations(input);

    const filteredOrganisations = organisations.filter(
      (orgItem) =>
        !selectedOrganisations.some((o) => o.id === orgItem.id) &&
        orgItem.id !== organisation.id,
    );

    return filteredOrganisations.map((organisation) => ({
      id: organisation.id,
      name: organisation.name,
      status: "NEW",
    }));
  };

  return (
    <div className="mb-4 space-y-4">
      <div className="space-y-2">
        <Label>Organisations</Label>
        <AsyncSelect<OrganisationOption>
          unstyled
          components={{
            DropdownIndicator: SearchIndicator,
            ClearIndicator: () => null,
            Option: OrganisationSelectOption,
            IndicatorSeparator: () => null,
          }}
          ref={form.register("roomSystems").ref}
          classNames={
            reactSelectClassNames as ClassNamesConfig<
              OrganisationOption,
              false,
              GroupBase<OrganisationOption>
            >
          }
          placeholder="Type organisation name..."
          noOptionsMessage={() => "Type organisation name..."}
          loadOptions={loadOptions}
          isDisabled={form.formState.isSubmitting}
          inputValue={value}
          onInputChange={(value) => {
            setValue(value);
          }}
          value={null}
          getOptionLabel={(option) => option.name}
          onChange={(value) => {
            if (value) {
              addOrganisation(value);
              setValue(""); // Clear the input once added
              return null; // Prevent adding to internal state
            }
          }}
        />
        {organisationsError && (
          <p className="text-sm font-medium text-destructive">
            {organisationsError.message}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          Organisation admins will receive an invitation, where they can assign
          users and room systems to this invitation. Invited organisations can
          manage this session.
        </p>
      </div>
    </div>
  );
}
